import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { billingAPI, patientAPI, invoiceAPI } from '../../lib/api';
import { toast } from 'sonner';
import { CreateBillDialog } from './components/CreateBillDialog';
import { BillsTable } from './components/BillsTable';
import { PaymentDialog } from './components/PaymentDialog';
import { ViewBillDialog } from './components/ViewBillDialog';
import { InvoiceDialog } from './components/InvoiceDialog';
import { apiErrorMessage } from '../../lib/utils';
import { logger } from '../../lib/logger';

let billItemKeySeq = 0;
const newBillItem = () => ({ _key: `item-${++billItemKeySeq}`, description: '', category: '', quantity: 1, unit_price: 0, total: 0 });

const BillingList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceSettings, setInvoiceSettings] = useState(null);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [paymentData, setPaymentData] = useState({ amount: 0, payment_method: 'cash', reference_number: '' });
  
  // Get patient_id from URL
  const queryParams = new URLSearchParams(location.search);
  const patientIdFromUrl = queryParams.get('patient_id');
  
  const [formData, setFormData] = useState({
    patient_id: patientIdFromUrl || '',
    items: [newBillItem()],
    discount_percent: 0,
    notes: '',
  });

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const [billRes, patRes] = await Promise.all([
        billingAPI.getAll(params),
        patientAPI.getAll({}),
      ]);
      setBills(billRes.data.bills || billRes.data || []);
      setPatients(patRes.data.patients || patRes.data || []);
    } catch (error) {
      logger.error('Error loading billing data:', error);
      toast.error(apiErrorMessage(error, 'Failed to load billing data. Please try again.'));
      setBills([]);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);


  useEffect(() => {
    fetchData();
  }, [statusFilter, fetchData]);
  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, newBillItem()],
    });
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index),
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
    }
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + item.total, 0);
    const discount = subtotal * (formData.discount_percent / 100);
    return { subtotal, discount, total: subtotal - discount };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await billingAPI.create({ ...formData, items: formData.items.map(({ _key, ...item }) => item) });
      toast.success('Bill created successfully');
      setDialogOpen(false);
      
      // If came from patient detail, navigate back to patient detail page
      if (patientIdFromUrl) {
        navigate(`/patients/${patientIdFromUrl}`);
      } else {
        setFormData({
          patient_id: '',
          items: [newBillItem()],
          discount_percent: 0,
          notes: '',
        });
        fetchData();
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to create bill'));
    }
  };

  const openPaymentDialog = (bill) => {
    setSelectedBill(bill);
    setPaymentData({
      bill_id: bill.id,
      amount: bill.total_amount - (bill.paid_amount || 0),
      payment_method: 'cash',
      reference_number: '',
    });
    setPaymentDialogOpen(true);
  };


  const handleGenerateInvoice = async (bill) => {
    setGeneratingInvoice(true);
    try {
      // Check if invoice already exists
      const existingInvoice = await invoiceAPI.getByBill(bill.id);
      
      if (existingInvoice && existingInvoice.data) {
        // Invoice already exists, just show it
        setSelectedInvoice(existingInvoice.data);
        setInvoiceDialogOpen(true);
      } else {
        // Generate new invoice
        const response = await invoiceAPI.generate({ bill_id: bill.id });
        setSelectedInvoice(response.data.invoice);
        setInvoiceDialogOpen(true);
        toast.success('Invoice generated successfully');
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to generate invoice'));
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const handlePrintInvoice = () => {
    window.print();
  };


  const handlePayment = async () => {
    try {
      await billingAPI.makePayment(selectedBill.id, paymentData);
      toast.success('Payment recorded successfully');
      setPaymentDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const viewBill = async (bill) => {
    try {
      const response = await billingAPI.getById(bill.id);
      setSelectedBill(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load bill details');
    }
  };

  const totals = calculateTotals();

  return (
    <div data-testid="billing-page">
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Billing</h1>
            <p className="text-slate-500">Manage patient bills and payments</p>
          </div>
          <CreateBillDialog patients={patients} dialogOpen={dialogOpen} setDialogOpen={setDialogOpen} patientIdFromUrl={patientIdFromUrl} formData={formData} setFormData={setFormData} addItem={addItem} removeItem={removeItem} updateItem={updateItem} handleSubmit={handleSubmit} totals={totals} navigate={navigate} />
        </div>
      </header>

      <div className="page-content">
        <BillsTable bills={bills} loading={loading} statusFilter={statusFilter} setStatusFilter={setStatusFilter} generatingInvoice={generatingInvoice} openPaymentDialog={openPaymentDialog} handleGenerateInvoice={handleGenerateInvoice} viewBill={viewBill} />
      </div>

      <PaymentDialog paymentDialogOpen={paymentDialogOpen} setPaymentDialogOpen={setPaymentDialogOpen} selectedBill={selectedBill} paymentData={paymentData} setPaymentData={setPaymentData} handlePayment={handlePayment} />
      <ViewBillDialog viewDialogOpen={viewDialogOpen} setViewDialogOpen={setViewDialogOpen} selectedBill={selectedBill} />
      <InvoiceDialog invoiceDialogOpen={invoiceDialogOpen} setInvoiceDialogOpen={setInvoiceDialogOpen} selectedInvoice={selectedInvoice} invoiceSettings={invoiceSettings} handlePrintInvoice={handlePrintInvoice} />
    </div>
  );
};

export default BillingList;
